import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import SEO from "../components/SEO";
import AuthShell from "../components/AuthShell";
import { toast } from "sonner";
import { ArrowRight, AlertCircle } from "lucide-react";
import PasswordInput from "../components/PasswordInput";

const RESET_HERO = "https://static.prod-images.emergentagent.com/jobs/8e4196d6-aa64-413e-8d09-93aaa7923fd4/images/81d661c59dd94452674038a69ba4e3d5fb9d6ff4518fc955a572c660c4272fe6.png";

export default function ResetPassword() {
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [loading, setLoading] = useState(false);
    const nav = useNavigate();

    const submit = async (e) => {
        e.preventDefault();
        if (password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
        if (password !== confirm) { toast.error("Passwords don't match"); return; }
        setLoading(true);
        try {
            await api.post("/auth/reset-password", { token, new_password: password });
            toast.success("Password updated! Log in with your new password.");
            nav("/login");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Reset failed. The link may have expired.");
        } finally {
            setLoading(false);
        }
    };

    if (!token) {
        return (
            <AuthShell
                testid="reset-missing-token-shell"
                heroImage={RESET_HERO}
                heroQuote="Take a breath. We’ll get you a fresh link."
                heading="Invalid reset link."
                sub="This reset link is missing a token. Request a new one below."
            >
                <SEO title="Set a new password — Energize.fit" path="/reset-password" noindex />
                <div data-testid="reset-missing-token">
                    <div className="h-14 w-14 rounded-full bg-red-50 text-red-500 flex items-center justify-center mb-5">
                        <AlertCircle className="w-7 h-7" />
                    </div>
                    <Link to="/forgot-password" className="inline-flex items-center gap-2 bg-stone-900 hover:bg-stone-800 text-white rounded-full px-6 py-3 font-medium transition-all" data-testid="reset-request-new">
                        Request a new link <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            </AuthShell>
        );
    }

    return (
        <>
            <SEO title="Set a new password — Energize.fit" path="/reset-password" noindex />
            <AuthShell
                testid="reset-shell"
                heroImage={RESET_HERO}
                heroQuote="A fresh start, one secure password away."
                heading="Set a new password."
                sub="Make it at least 8 characters. Something memorable to you."
            >
                <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
                    <div>
                        <label className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500 mb-2 block">New password</label>
                        <PasswordInput value={password} onChange={setPassword} required autoComplete="new-password" testId="reset-password" minLength={8} className="w-full bg-white border border-stone-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-teal-500/40 focus:border-teal-500 outline-none transition" />
                    </div>
                    <div>
                        <label className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500 mb-2 block">Confirm password</label>
                        <PasswordInput value={confirm} onChange={setConfirm} required autoComplete="new-password" testId="reset-confirm" minLength={8} className="w-full bg-white border border-stone-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-teal-500/40 focus:border-teal-500 outline-none transition" />
                    </div>
                    <button type="submit" disabled={loading} data-testid="reset-submit" className="w-full inline-flex items-center justify-center gap-2 bg-stone-900 hover:bg-stone-800 text-white rounded-full px-6 py-4 font-medium hover:-translate-y-0.5 transition-all disabled:opacity-60">
                        {loading ? "Updating…" : <>Update password <ArrowRight className="w-4 h-4" /></>}
                    </button>
                </form>
            </AuthShell>
        </>
    );
}
